import React, { useContext, useEffect, useRef, useState } from 'react';
import { Modal, Tabs, Flex, Button, Input, Space, Tag } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import SText from '@/styles/SText';
import { InventoryLotItem, InventoryLocationItem, LocationLockEnum, OrderAddV2 } from '@/types/inventoryV2';
import { SearchOutlined } from '@ant-design/icons';
import useDataList from '@/hooks/useDataList';
import { productLocations, productLots } from '@/apis/inventoryv2';
import { CommonListParams } from '@/types/common';
import STableLayout, { DEFAULT_PAGE_SIZE, TableLayoutRef } from '@/styles/STableLayout';
import { getLocationCode } from '@/pages/Warehouse/utils';
import { getLocationIdx } from '@/utils/wLocation';
import { AnyObject } from 'antd/es/_util/type';
import BarcodeList from './BarcodeList';
import MoveLocationForm from '../../Inventory/components/MoveLocationForm';
import LocationOutbound from '../../Inventory/components/LocationOutbound';
import { Product } from '@/types/product';
import useLabelColor from '../hooks/useLabelColor';
import { numFormmater } from '@/pages/Requirement/util';
import { DictContext } from '@/layout/context/DictContext';

export type MaterialType = "COMMON" | "SPECIAL";
interface MaterialDetailProps {
  product: Product;
  productId: string;
  onClose: () => void;
  type: MaterialType;
}

enum TabEnum {
  BATCH = '1',
  LOCATION = '2'
}


const MaterialDetail: React.FC<MaterialDetailProps> = ({
  product,
  productId,
  onClose,
  type
}) => {
  const { t } = useTranslation();
  const tableRef = useRef<TableLayoutRef | null>(null);
  const { getLabelColor } = useLabelColor();
  const { locationTypes } = useContext(DictContext);

  const [activeTab, setActiveTab] = useState<TabEnum>(TabEnum.BATCH);
  const [bList, bTotal, bLoading, { fetchList: getBList }] = useDataList<InventoryLotItem, [string, MaterialType, CommonListParams]>({ fetchApi: productLots });
  const [lList, lTotal, lLoading, { fetchList: getLList }] = useDataList<InventoryLocationItem, [string, MaterialType, CommonListParams]>({ fetchApi: productLocations });
  const [barcodeInfo, setBarcodeInfo] = useState<{ id: string; type: 'inventory' | 'location' } | null>(null);
  const [keyword, setKeyword] = useState<string>('');
  const [locItem, setLocItem] = useState<InventoryLocationItem | null>(null);
  const [outboundItem, setOutboundItem] = useState<InventoryLocationItem | null>(null);
  const [batchOutboundItem, setBatchOutboundItem] = useState<InventoryLotItem | null>(null);


  useEffect(() => {
    if (productId) {
      setKeyword('');
      tableRef?.current?.resetTablePage();
      fetch(0, '');
    }
  }, [productId, activeTab]);

  const tableTabs = [
    {
      key: TabEnum.BATCH,
      label: t('warehouse.batchList'),
    },
    {
      key: TabEnum.LOCATION,
      label: t('warehouse.locationList'),
    },
  ];

  const batchColumns: ColumnsType<InventoryLotItem> = [
    {
      title: t('warehouse.batchNo'),
      key: 'batchNo',
      width: 220,
      fixed: 'left',
      render: (_, record) => <SText text={record.lot?.lot || ''} />
    },
    {
      title: t('warehouse.supplierBatchNo'),
      key: 'supplierBatchNo',
      width: 150,
      render: (_, record) => <SText text={record.lot?.thdLot || ''} />
    },
    {
      title: t('purchase.productInfo'),
      children: [
        {
          title: <SText text={t('purchase.brand')} />,
          key: 'originalBrand',
          width: 120,
          render: (_, record) => <SText text={record.ext?.originalBrand || ''} style={{ width: "100%" }} />
        },
        {
          title: <SText text={t('purchase.mpn')} />,
          key: 'originalCode',
          width: 120,
          render: (_, record) => <SText text={record.ext?.originalCode || ''} style={{ width: "100%" }} />
        },
        // {
        //   title: <SText text={t('purchase.dcc')} />,
        //   key: 'dcc',
        //   width: 110,
        //   render: (_, record) => <SText text={record.ext?.dcc != null ? String(record.ext.dcc) : ''} style={{ width: "100%" }} />
        // },
        {
          title: <SText text={t('product.code')} />,
          key: 'productCode',
          width: 120,
          render: (_, record) => <SText text={record.product?.code || ''} style={{ width: "100%" }} />
        },
        {
          title: <SText text={t('product.name')} />,
          key: 'productName',
          width: 150,
          render: (_, record) => <SText text={record.product?.name || ''} style={{ width: "100%" }} />
        },
        {
          title: <SText text={t('product.brand1')} />,
          key: 'model',
          width: 120,
          render: (_, record) => <SText text={record.product?.model || ''} style={{ width: "100%" }} />
        },
        {
          title: <SText text={t('product.uom')} />,
          key: 'uom',
          width: 80,
          render: (_, record) => <SText text={record.product?.uom || ''} style={{ width: "100%" }} />
        },
      ]
    },
    {
      title: t('warehouse.location'),
      dataIndex: 'locations',
      key: 'locations',
      width: 200,
      render: (_, record) => (
        <SText text={record.locations?.map(loc => {
          let info = locationTypes.find(i => i.key === loc.location.type)
          return `${loc.location.number}(${info?.desc})`
        })?.join(', ') ?? ''} />
      )
    },
    {
      title: t('order.quantity'),
      dataIndex: 'count',
      key: 'count',
      width: 120,
      fixed: 'right',
      render: (value) => numFormmater(value || 0)
    },
    {
      title: t('warehouse.preIn'),
      dataIndex: 'preIn',
      key: 'preIn',
      width: 120,
      fixed: 'right',
      render: (value) => numFormmater(value || 0)
    },
    {
      title: t('warehouse.preOut'),
      dataIndex: 'preOut',
      key: 'preOut',
      width: 120,
      fixed: 'right',
      render: (value) => numFormmater(value || 0)
    },
    {
      title: t('common.opt'),
      fixed: 'right',
      width: 160,
      render: (_, record) => (
        <Space>
          <Button type="link" onClick={() => setBarcodeInfo({ id: record.productId, type: 'inventory' })}>
            {t('warehouse.viewBarcode')}
          </Button>
          {record.count > record.preOut && (
            <Button type="primary" onClick={() => setBatchOutboundItem(record)}>
              {t('warehouse.outbound')}
            </Button>
          )}
        </Space>
      )
    },
  ];

  const locationColumns: ColumnsType<InventoryLocationItem> = [
    // {
    //   title: t('warehouse.locationCode'),
    //   key: 'locationId',
    //   width: 180,
    //   fixed: 'left',
    //   render: (_, record) => <SText text={getLocationCode(record.location)} />
    // },
    {
      title: t("warehouse.locationIndex"),
      key: "l1",
      width: 120,
      render: (_, record) => (
        <SText text={getLocationIdx(record.location)} />
      )
    },
    {
      title: t("warehouse.labels"),
      key: "labels",
      width: 160,
      render: (_, record) => (
        <Flex gap={4} wrap="wrap">
          {record?.location?.label1 && <SText textColor="#FFF" tagMode color={getLabelColor("label1")} text={record.location.label1} />}

        </Flex>
      )
    },
    {
      title: t("inventory.locationType"),
      key: "type",
      width: 140,
      render: (_, record) => {
        let info = locationTypes.find(label => label.key === record.location.type);
        return (
          info && <SText text={info.desc} />
        )
      }
    },
    {
      title: t('warehouse.batchNo'),
      key: 'batchNo',
      width: 220,
      render: (_, record) => <SText text={record.lotItem?.lot?.lot || ''} />
    },
    {
      title: t('warehouse.supplierBatchNo'),
      key: 'supplierBatchNo',
      width: 150,
      render: (_, record) => <SText text={record.lotItem?.lot?.thdLot || ''} />
    },
    {
      title: <SText text={t('purchase.brand')} />,
      key: 'originalBrand',
      width: 120,
      render: (_, record) => <SText text={record.lotItem?.ext?.originalBrand || ''} style={{ width: "100%" }} />
    },
    {
      title: <SText text={t('purchase.mpn')} />,
      key: 'originalCode',
      width: 120,
      render: (_, record) => <SText text={record.lotItem?.ext?.originalCode || ''} style={{ width: "100%" }} />
    },
    // {
    //   title: <SText text={t('purchase.dcc')} />,
    //   key: 'dcc',
    //   width: 110,
    //   render: (_, record) => <SText text={record.lotItem?.ext?.dcc != null ? String(record.lotItem.ext.dcc) : ''} style={{ width: "100%" }} />
    // },
    {
      title: t('order.quantity'),
      dataIndex: 'count',
      key: 'count',
      width: 80,
      fixed: 'right',
      render: (value) => numFormmater(value || 0)
    },
    {
      title: t('warehouse.preIn'),
      dataIndex: 'preIn',
      key: 'preIn',
      width: 80,
      fixed: 'right',
      render: (value) => numFormmater(value || 0)
    },
    {
      title: t('warehouse.preOut'),
      dataIndex: 'preOut',
      key: 'preOut',
      width: 80,
      fixed: 'right',
      render: (value) => numFormmater(value || 0)
    },
    {
      title: t('common.opt'),
      fixed: 'right',
      width: 160,
      render: (_, record) => (
        <Space>
          <Button type="link" onClick={() => setBarcodeInfo({ id: record.locationId, type: 'location' })}>
            {t('warehouse.viewBarcode')}
          </Button>
          {record.location.lockState !== LocationLockEnum.LOCKED && (
            <>
              <Button onClick={() => setLocItem({ ...record, lotItem: { ...record.lotItem, product } })}>{t('warehouse.moveLoc')}</Button>
              {record.count > record.preOut && <Button type="primary" onClick={() => setOutboundItem(record)}>
                {t('warehouse.outbound')}
              </Button>}
            </>
          )}
        </Space>
      )
    },
  ];

  const fetch = (offset: number, kw: string) => {
    const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE;
    let params: CommonListParams = {
      pagination: { offset, take },
      body: kw,
    }
    if (productId) {
      if (activeTab === TabEnum.BATCH) {
        getBList(productId, type, { ...params })
      } else {
        getLList(productId, type, { ...params })
      }
    }
  };

  const outSucc = (data: OrderAddV2) => {
    // 重新请求数据
    fetch(0, keyword);
    setOutboundItem(null);
    setBatchOutboundItem(null);
  }

  return (
    <Modal
      title={t('warehouse.materialDetail')}
      open={!!productId}
      onCancel={onClose}
      footer={null}
      width={1200}
      destroyOnClose
    >
      <Flex vertical align='flex-start' style={{ height: '70vh' }} >
        <Flex justify='space-between' style={{ width: "100%", marginBottom: 16 }} align='center'>
          <Tabs
            activeKey={activeTab}
            onChange={(key) => setActiveTab(key as TabEnum)}
            type="card"
            items={tableTabs}
          />
          <Flex gap={10} align='center'>
            <Input
              placeholder={t('warehouse.searchBatchPlaceholder')}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value.trim())}
              onPressEnter={() => {
                tableRef?.current?.resetTablePage();
                fetch(0, keyword);
              }}
              style={{ width: 250 }}
            />
            <Button
              type="primary"
              size="small"
              icon={<SearchOutlined />}
              onClick={() => {
                tableRef?.current?.resetTablePage();
                fetch(0, keyword);
              }}
            />
          </Flex>
        </Flex>
        <STableLayout
          headerChildren={null}
          tableLayoutRef={tableRef}
          total={activeTab === TabEnum.BATCH ? bTotal : lTotal}
          handleChange={(offset) => { fetch(offset, keyword) }}
          columns={(activeTab === TabEnum.BATCH ? batchColumns : locationColumns) as ColumnsType<AnyObject>}
          dataSource={activeTab === TabEnum.BATCH ? bList : lList.filter(lot => lot.preIn > 0 || lot.preOut > 0 || lot.count > 0)}
          loading={activeTab === TabEnum.BATCH ? bLoading : lLoading}
          scrollX={1350}
        />
      </Flex>
      {barcodeInfo && <BarcodeList id={barcodeInfo.id} type={barcodeInfo.type} onClose={() => setBarcodeInfo(null)} />}
      {locItem && <MoveLocationForm locItem={locItem} onClose={() => setLocItem(null)} onOk={() => { setLocItem(null); fetch(0, keyword) }} />}
      {outboundItem && (
        <LocationOutbound
          lotItems={[{ ...outboundItem, lotItem: { ...outboundItem.lotItem, product } }]}
          open={!!outboundItem}
          onCancel={() => setOutboundItem(null)}
          locationNo={getLocationCode(outboundItem.location)}
          onSuccess={outSucc}
        />
      )}
      {batchOutboundItem && batchOutboundItem.locations && (
        <LocationOutbound
          lotItems={
            batchOutboundItem.locations
              .filter(loc => loc.count > loc.preOut && loc.location.lockState === LocationLockEnum.UN_LOCKED)
              .map(loc => ({
                id: `${batchOutboundItem.id}-${loc.location.id}`,
                state: batchOutboundItem.state,
                createdAt: batchOutboundItem.createdAt,
                createdBy: batchOutboundItem.createdBy,
                updatedAt: loc.updatedAt,
                updatedBy: loc.updatedBy,
                creator: batchOutboundItem.creator,
                updater: batchOutboundItem.updater as any,
                barcodes: null,
                count: loc.count,
                preIn: loc.preIn,
                preOut: loc.preOut,
                locationId: loc.location.id,
                location: loc.location,
                lotItemId: batchOutboundItem.id,
                lotItem: batchOutboundItem,
              }))

          }
          open={!!batchOutboundItem}
          onCancel={() => setBatchOutboundItem(null)}
          onSuccess={outSucc}
        />
      )}

    </Modal>
  );
};

export default MaterialDetail;
